import {getCurrentFocus, restoreFocus} from './notification-list-focus'
import {fetchSafeDocumentFragment} from '@github-ui/fetch-utils'
import {hasInteractions} from '@github-ui/has-interactions'
import {updateSelectedNotificationsInBulkActionForms} from './bulk-action-dropdowns'

const DEBOUNCE_DELAY = 500
let currentUpdateId = 1

// Creates and returns a unique update id
export function getUpdateId(): number {
  currentUpdateId = (currentUpdateId + 1) % 10000
  return currentUpdateId
}

function isValidUpdateId(updateId: number): boolean {
  return currentUpdateId === updateId
}

// Immediately invalidates any other update sequences
export function invalidateUpdates() {
  getUpdateId()
}

function getUpdateUrl(document: Document | DocumentFragment): string {
  const container = document.querySelector<HTMLElement>('.js-notifications-container')!
  return container.getAttribute('data-url')!
}

/*
 * As the user navigates with PJAX, the current update url can change
 * this function validates that the one we have is still correct against
 * the document currently.
 */
function isValidUpdateUrl(updateUrl: string): boolean {
  return updateUrl === getUpdateUrl(document)
}

// Refetch the index page and update the current page with the latest content
//
// Before fetching and rendering updates, this function will validate that
// no other code has requested an update, and that the current notification
// list url has not changed. This prevents rendering stale content from an old
// server response into the UI.
//
// Additionally, fetches will be debounced by the DEBOUNCE_DELAY to prevent lots of
// server requests if the user performs many actions quickly.
//
// Arguments:
//   * updateId [optional]
//     - pass an updateId from getUpdateId() if you need to do some async work between
//       invalidating existing updates and actually triggering the update
export async function updateNotifications(updateId: number = getUpdateId()) {
  const url = getUpdateUrl(document)
  let fragment

  // Verify that the updateId passed is still valid
  if (!isValidUpdateId(updateId)) return

  // Timeout to allow for other requests to come in for a short period of time
  await new Promise(resolve => setTimeout(resolve, DEBOUNCE_DELAY))
  // If another update request happened in the meantime, cancel this one
  if (!isValidUpdateId(updateId)) return

  try {
    // Verify that the url we want to update from is still the valid url we are showing
    if (!isValidUpdateUrl(url)) return
    fragment = await fetchSafeDocumentFragment(document, url)
  } catch {
    return
  }

  // If another update request happened in the meantime, cancel this one
  if (!isValidUpdateId(updateId)) return
  const currentFocus = getCurrentFocus()
  updateContent(fragment)
  // Only restore the keyboard focus based on position, as the previously
  // selected notification may have jumped around
  restoreFocus({position: currentFocus.position})

  updateSelectedNotificationsInBulkActionForms()
}

/*
 * Update the current document with the elements from the new server-rendered html.
 *
 * We do this in small chunks to reduce the impact of a user interacting with a sub-set of
 * the UI.
 */
function updateContent(fragment: DocumentFragment) {
  // Don't render results if the page has changed via pjax
  if (!isValidUpdateUrl(getUpdateUrl(fragment))) {
    return
  }

  replaceContent('.js-notification-inboxes', fragment)

  replaceContent('.js-notification-new-filters', fragment)
  replaceContent('.js-notification-mobile-filters', fragment)

  replaceContent('.js-notification-sidebar-repositories', fragment)
  replaceContent('.js-notifications-list', fragment)
  replaceContent('.js-notifications-mark-selected-actions', fragment)
  replaceContent('.js-notifications-mark-all-actions', fragment)

  replaceContent('.js-notifications-list-paginator-counts', fragment)
  replaceContent('.js-notifications-list-paginator-buttons', fragment)
  replaceContent('.js-notifications-list-protip', fragment, (newContent, oldContent) => {
    // don't update the protip if it has just _changed_ (i.e. both old and new are not empty)
    if (newContent.children.length > 0 && oldContent.children.length > 0) return false

    return true
  })
}

/*
 * Replace the contents of the specific selector from the incoming fragment
 * into the document.
 *
 * If the user is currently interacting with the contents of the selector, it will not
 * be replaced.
 */
function replaceContent(
  selector: string,
  fragment: DocumentFragment,
  filterUpdate?: (newContent: Element, oldContent: Element) => boolean,
) {
  const newContent = fragment.querySelector(selector)
  if (!newContent) return

  const toBeReplaced = document.querySelector(selector)
  if (!toBeReplaced) return
  // Do not replace if user is currently interacting with content
  if (hasInteractions(toBeReplaced, true)) return
  // Do not replace if content hasn't changed
  if (newContent.outerHTML === toBeReplaced.outerHTML) return

  // If filterUpdate returns false, skip update
  if (filterUpdate && !filterUpdate(newContent, toBeReplaced)) return

  toBeReplaced.replaceWith(newContent)
}
